import { Typography } from '@material-ui/core';
import {
  AccountBalance,
  Business,
  DirectionsCar,
  Error,
  Image,
  Language,
  WarningRounded,
} from '@material-ui/icons';
import { ColorDynamic, Inline, Stack, Tag } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useNavigate } from 'react-router-dom';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { CashFastIcon, CashIcon, TextBoxIcon } from 'shared/icons';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import {
  useCarrierInfo,
  useIsCarrierWithoutUSDOT,
} from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import { CarrierOverviewDecliningReasons } from 'shared/modules/carrier-profile/core/CarrierOverviewDeclineReasons';
import { CarrierOverviewRevokingReasons } from 'shared/modules/carrier-profile/core/CarrierOverviewRevokeReasons';
import { useVerifiedCarrierApplicationStatus } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusAPI';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import styled from 'styled-components';
import {
  getInsuranceExpiredForUnverifiedCarrier,
  getInsuranceExpiring,
} from '../../profile/core/CargoInsuranceExpiring';
import { VerifiedCarrierApplicationStatus } from '../../profile/core/VerifiedCarrierApplicationStatus';
import { phoneVerificationStatuses } from '../../profile/ProfileSidebar';
import { CarrierWebviewHeader } from '../CarrierWebviewHeader';
import { CarrierProfileWebviewHeader } from './CarrierProfileWebviewHeader';

const Wrapper = styled.div`
  display: flex;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid ${ColorDynamic.Silver400};
  justify-content: space-between;
`;

interface CarrierProfilePageItemProps {
  icon: React.ReactNode;
  label: string;
  route: string;
  hasError?: boolean;
  hasWarning?: boolean;
  shouldShowNewTag?: boolean;
  shouldShowToDoTag?: boolean;
}

export function CarrierProfilePageItem({
  icon,
  label,
  route,
  hasError,
  hasWarning,
  shouldShowNewTag,
  shouldShowToDoTag,
}: CarrierProfilePageItemProps) {
  const navigate = useNavigate();

  return (
    <Wrapper
      onClick={() => {
        navigate(route);
      }}
    >
      <Inline space="small" verticalAlign="center">
        {icon}
        <Typography>{label}</Typography>
      </Inline>
      <Inline>
        {shouldShowNewTag && (
          <Tag color="blue" variant="subtle">
            New
          </Tag>
        )}
        {shouldShowToDoTag && (
          <Tag color="yellow" variant="subtle">
            To Do
          </Tag>
        )}
        {hasError ? (
          <Error
            aria-label={`${label} Error`}
            htmlColor={ColorDynamic.Red300}
            fontSize="small"
          />
        ) : hasWarning ? (
          <WarningRounded
            aria-label={`${label} WarningRounded`}
            htmlColor={ColorDynamic.Yellow300}
            fontSize="small"
          />
        ) : undefined}
      </Inline>
    </Wrapper>
  );
}

export function ProfileWebviewPage() {
  const isCarrierWithoutUSDOT = useIsCarrierWithoutUSDOT();
  const { data: carrierInfo } = useCarrierInfo();
  const { data: carrierSettings } = useCarrierSettings();
  const { data } = useVerifiedCarrierApplicationStatus();
  const verifiedCarrierApplication = data?.verified_carrier_application;
  const decliningReasons = verifiedCarrierApplication?.declining_reasons;
  const revokingReasons = verifiedCarrierApplication?.revoking_reasons;

  const isVerified = verifiedCarrierApplication?.status === 'approved';
  const superPayVerificationStatus = carrierSettings?.superpay.verification;
  const isSuperPayVerified = superPayVerificationStatus === 'verified';
  const isSuperPayOnboardingNotCompleted = isVerified && !isSuperPayVerified;
  const isSuperPayOnboardingError =
    superPayVerificationStatus === 'bank_account_failed' ||
    superPayVerificationStatus === 'bank_account_errored';
  const isSuperPayBankError = !!carrierSettings?.superpay.payment_error;
  const isSuperPayError = isSuperPayOnboardingError || isSuperPayBankError;
  const isSuperPayToDo = !isSuperPayError && isSuperPayOnboardingNotCompleted;
  const isInsuranceNeedsRenewal =
    isVerified &&
    !carrierSettings?.carrier.is_insurance_renewal_initiated &&
    !!carrierSettings?.carrier.cargo_insurance_expiration_date &&
    getInsuranceExpiring(
      carrierSettings.carrier.cargo_insurance_expiration_date,
    );
  const isInsuranceExpiredForUnverifiedCarrier =
    carrierSettings &&
    getInsuranceExpiredForUnverifiedCarrier(carrierSettings.carrier);
  const isW9WasRemoved =
    verifiedCarrierApplication?.status !== 'not_submitted' &&
    !verifiedCarrierApplication?.progress.w9_step_is_complete;

  const isFmcsaPhoneNeedVerification =
    !carrierInfo?.fmcsa_phone_number?.is_verified &&
    verifiedCarrierApplication &&
    !phoneVerificationStatuses.includes(verifiedCarrierApplication.status);

  const hasOverviewError =
    !!decliningReasons?.reliability_score?.length ||
    !!revokingReasons?.terms_and_conditions?.length;
  const hasCargoInsuranceWarning =
    verifiedCarrierApplication?.status === 'reviewed' ||
    isInsuranceNeedsRenewal;
  const hasCargoInsuranceError =
    !!decliningReasons?.cargo_insurance_file_url?.length ||
    !!revokingReasons?.cargo_insurance_file_url?.length ||
    isInsuranceExpiredForUnverifiedCarrier;
  const hasW9Error = !!decliningReasons?.w9_form_url?.length || isW9WasRemoved;
  const hasTransporterPlateError =
    !!decliningReasons?.transporter_plate_registration?.length;
  const hasStatePermitError =
    !!decliningReasons?.state_motor_carrier_permit?.length;
  const hasBusinessLicenseError = !!decliningReasons?.business_license?.length;
  const isEditingEnabled =
    !carrierSettings?.drivers_can_edit_carrier_info &&
    MobileAppBridge.isInjected();

  const hasCarrierInfoError =
    !!revokingReasons?.fmcsa_phone?.length ||
    !!carrierInfo?.fmcsa_phone_number?.grace_period_ends_at;

  useDocumentTitle('Carrier Profile');

  return (
    <CarrierWebviewHeader>
      <Box
        width="100%"
        maxWidth="832px"
        padding="small"
        marginLeft="auto"
        marginRight="auto"
      >
        <Stack space="small">
          <CarrierProfileWebviewHeader />
          {data && <VerifiedCarrierApplicationStatus {...data} />}

          <CarrierOverviewDecliningReasons />

          <CarrierOverviewRevokingReasons />

          <Stack space="xxsmall">
            <CarrierProfilePageItem
              icon={
                <Language fontSize="small" htmlColor={ColorDynamic.Dark300} />
              }
              label="Overview"
              route="/profile/overview"
              hasError={hasOverviewError}
            />

            <CarrierProfilePageItem
              icon={
                <Business fontSize="small" htmlColor={ColorDynamic.Dark300} />
              }
              label="Carrier Info"
              route="/profile/carrier-info"
              shouldShowToDoTag={isFmcsaPhoneNeedVerification}
              hasError={hasCarrierInfoError}
            />

            <CarrierProfilePageItem
              icon={
                <DirectionsCar
                  fontSize="small"
                  htmlColor={ColorDynamic.Dark300}
                />
              }
              label="Cargo Insurance"
              route="/profile/cargo-insurance"
              hasWarning={hasCargoInsuranceWarning}
              hasError={hasCargoInsuranceError}
            />

            <CarrierProfilePageItem
              icon={
                <CashIcon fontSize="small" htmlColor={ColorDynamic.Dark300} />
              }
              label="Billing Info"
              route="/profile/billing-info"
            />

            <CarrierProfilePageItem
              icon={<Image fontSize="small" htmlColor={ColorDynamic.Dark300} />}
              label="W-9 Form"
              route="/profile/w9"
              hasError={hasW9Error}
            />

            {carrierInfo?.logistics_type === 'driveaway' && (
              <CarrierProfilePageItem
                icon={
                  <Image fontSize="small" htmlColor={ColorDynamic.Dark300} />
                }
                label="Transporter Plate Registration"
                route="/profile/transporter-plate-registration"
                hasError={hasTransporterPlateError}
              />
            )}

            {isCarrierWithoutUSDOT &&
              (carrierInfo?.logistics_type === 'auto_hauler' ||
                carrierInfo?.logistics_type === 'towing') && (
                <CarrierProfilePageItem
                  icon={
                    <Image fontSize="small" htmlColor={ColorDynamic.Dark300} />
                  }
                  label="State Motor Carrier Permit"
                  route="/profile/state-motor-carrier-permit"
                  hasError={hasStatePermitError}
                />
              )}

            {isCarrierWithoutUSDOT &&
              carrierInfo?.logistics_type === 'driveaway' && (
                <CarrierProfilePageItem
                  icon={
                    <Image fontSize="small" htmlColor={ColorDynamic.Dark300} />
                  }
                  label="Business License"
                  route="/profile/business-license"
                  hasError={hasBusinessLicenseError}
                />
              )}

            <CarrierProfilePageItem
              icon={
                <AccountBalance
                  fontSize="small"
                  htmlColor={ColorDynamic.Dark300}
                />
              }
              label="ACH Payment Info"
              route="/profile/ach-payment"
            />

            {!isEditingEnabled && (
              <CarrierProfilePageItem
                icon={
                  <CashFastIcon
                    fontSize="small"
                    htmlColor={ColorDynamic.Dark300}
                  />
                }
                label="SuperPay"
                route="/superpay"
                shouldShowToDoTag={isSuperPayToDo}
                hasError={isSuperPayError}
              />
            )}

            <CarrierProfilePageItem
              icon={
                <TextBoxIcon
                  fontSize="small"
                  htmlColor={ColorDynamic.Dark300}
                />
              }
              label="Terms and Conditions"
              route="/profile/terms-and-conditions"
            />
          </Stack>
        </Stack>
      </Box>
    </CarrierWebviewHeader>
  );
}
